import { useEffect, useRef, useState, useCallback } from "react";

const PREFIX = "somcore:draft:";

// Guards a form against accidental loss:
//  1. Persists a local draft (localStorage) whenever the form differs from the last saved state.
//  2. Warns the browser before the tab is closed/reloaded while there are unsaved changes.
//  3. Surfaces a recovered draft from a previous session so the user can restore or discard it.
// `snapshot` must be a serialisable object of just the editable fields.
export function useDraftGuard(storageKey, snapshot, { enabled = true } = {}) {
  const fullKey = PREFIX + storageKey;
  const baselineRef = useRef(null);
  const initedRef = useRef(false);
  const pendingRebaselineRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [recovered, setRecovered] = useState(null);

  // Initialise baseline and recover any prior-session draft (once, when ready).
  useEffect(() => {
    if (!enabled || initedRef.current || snapshot == null) return;
    initedRef.current = true;
    baselineRef.current = JSON.stringify(snapshot);
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw && raw !== baselineRef.current) setRecovered(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [enabled, snapshot, fullKey]);

  // Track dirty state and persist the draft.
  useEffect(() => {
    if (!initedRef.current || snapshot == null) return;
    const cur = JSON.stringify(snapshot);
    if (pendingRebaselineRef.current) {
      pendingRebaselineRef.current = false;
      baselineRef.current = cur;
      setDirty(false);
      try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
      return;
    }
    if (cur !== baselineRef.current) {
      setDirty(true);
      try { localStorage.setItem(fullKey, cur); } catch { /* ignore */ }
    } else {
      setDirty(false);
      try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
    }
  }, [snapshot, fullKey]);

  // Warn before closing/reloading the tab with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const restore = useCallback(() => {
    const d = recovered;
    setRecovered(null);
    return d;
  }, [recovered]);

  const discard = useCallback(() => {
    setRecovered(null);
    try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
  }, [fullKey]);

  // Call right after a successful save; re-baselines against the next snapshot.
  const markSaved = useCallback(() => {
    pendingRebaselineRef.current = true;
    setRecovered(null);
    setDirty(false);
  }, []);

  return { dirty, recovered, restore, discard, markSaved };
}
